import { useState, useCallback, useEffect } from "react";
import Sidebar, { type Project } from "./components/Sidebar";
import ChatPanel from "./components/ChatPanel";
import TopBar from "./components/TopBar";
import CommandPalette from "./components/CommandPalette";
import LoadingScreen from "./components/LoadingScreen";
import LoginPage from "./components/LoginPage";
import BootstrapPage from "./components/BootstrapPage";
import AdminUsersPage from "./components/AdminUsersPage";
import ChangePasswordPage from "./components/ChangePasswordPage";
import { useMe } from "./hooks/useMe";
import { HttpError } from "./api/errors";
import { needsBootstrap } from "./api/auth";

export default function App() {
  const me = useMe();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [view, setView] = useState<"chat" | "admin">("chat");
  const [bootstrap, setBootstrap] = useState<boolean | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [cmdkOpen, setCmdkOpen] = useState(false);

  // ⌘K / Ctrl+K toggles the command palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdkOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // When unauthenticated, ask the server if bootstrap is needed.
  useEffect(() => {
    const unauth = me.error instanceof HttpError && me.error.status === 401;
    if (!unauth) {
      setBootstrap(null);
      return;
    }
    let cancelled = false;
    needsBootstrap()
      .then((b) => { if (!cancelled) setBootstrap(b); })
      .catch(() => { if (!cancelled) setBootstrap(false); });
    return () => { cancelled = true; };
  }, [me.error]);

  const handleSelect = useCallback((id: string) => {
    setSelectedId(id);
    setSidebarOpen(false);
  }, []);

  const handleProjectsLoaded = useCallback((loaded: Project[]) => {
    setProjects(loaded);
    setSelectedId((cur) => cur ?? (loaded.length > 0 ? loaded[0].id : null));
  }, []);

  if (me.loading) return <LoadingScreen />;

  const unauthorized = me.error instanceof HttpError && me.error.status === 401;
  if (unauthorized || !me.data) {
    if (bootstrap === null) return <LoadingScreen />;
    return bootstrap ? <BootstrapPage /> : <LoginPage />;
  }

  if (me.data.requirePasswordChange) {
    return <ChangePasswordPage />;
  }

  if (view === "admin" && me.data.role === "admin") {
    return <AdminUsersPage onClose={() => setView("chat")} />;
  }

  const showEmptyMember =
    me.data.role === "member" && me.data.projects.length === 0;

  return (
    <div className="layout">
      <TopBar
        me={me.data}
        loading={me.loading}
        error={me.error}
        onToggleSidebar={() => setSidebarOpen((o) => !o)}
        onManageUsers={() => setView("admin")}
        onOpenCmdk={projects.length > 0 ? () => setCmdkOpen(true) : undefined}
      />
      <aside className={`sidebar${sidebarOpen ? " open" : ""}`}>
        <h2 className="sidebar-title">Projects</h2>
        {showEmptyMember ? (
          <p className="empty-projects">
            You do not have access to any projects yet. Ask an admin to assign
            you a project in the Users panel.
          </p>
        ) : (
          <Sidebar
            selectedId={selectedId}
            onSelect={handleSelect}
            onProjectsLoaded={handleProjectsLoaded}
          />
        )}
      </aside>
      {sidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <main className="chat">
        {selectedId ? (
          <ChatPanel project={selectedId} key={selectedId} />
        ) : (
          <div className="chat-empty">Select a project to start chatting.</div>
        )}
      </main>
      <CommandPalette
        open={cmdkOpen}
        onClose={() => setCmdkOpen(false)}
        projects={projects}
        onSelectProject={(id) => {
          handleSelect(id);
          setView("chat");
        }}
      />
    </div>
  );
}
